import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { HardHat, Plus, Pencil, Trash2, X, Check } from "lucide-react";
import ConfirmDialog from "../components/ConfirmDialog";

export default function ProjectsDialog({ projects, onCreate, onUpdate, onDelete }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [delId, setDelId] = useState(null);

  const reset = () => { setName(""); setEditing(null); };

  const submit = async () => {
    if (!name.trim()) return;
    if (editing) await onUpdate(editing, name.trim());
    else await onCreate(name.trim());
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="open-projects" className="border-slate-300">
          <HardHat size={15} className="mr-1.5" /> Obras
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md rounded-sm">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">Administrar obras / proyectos</DialogTitle>
          <DialogDescription>Crea, renombra o elimina obras. Solo puedes eliminar obras sin trabajadores.</DialogDescription></DialogHeader>

        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label className="text-xs">{editing ? "Editar obra" : "Nueva obra"}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="project-name"
              onKeyDown={(e) => e.key === "Enter" && submit()} className="h-9" placeholder="Ej. Torre Norte" />
          </div>
          {editing && <Button variant="ghost" size="sm" onClick={reset} className="h-9"><X size={15} /></Button>}
          <Button size="sm" onClick={submit} data-testid="project-save" className="h-9 bg-blue-600 hover:bg-blue-700">
            {editing ? <Check size={15} /> : <Plus size={15} />}
          </Button>
        </div>

        <div className="max-h-72 overflow-auto border border-slate-200 rounded-sm divide-y divide-slate-100">
          {projects.length === 0 && <p className="p-4 text-center text-slate-400 text-sm">Sin obras registradas</p>}
          {projects.map((p) => (
            <div key={p.id} className="flex items-center justify-between p-2.5" data-testid={`project-row-${p.id}`}>
              <div>
                <span className="font-medium text-slate-800 text-sm">{p.name}</span>
                <span className="ml-2 text-xs text-slate-400">{p.worker_count || 0} trabajador(es)</span>
              </div>
              <div className="whitespace-nowrap">
                <button onClick={() => { setEditing(p.id); setName(p.name); }} className="text-slate-500 hover:text-blue-600 mr-2" data-testid={`project-edit-${p.id}`}><Pencil size={15} /></button>
                <button onClick={() => setDelId(p.id)} className="text-slate-500 hover:text-red-600" data-testid={`project-delete-${p.id}`}><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400">Una obra solo puede eliminarse si no tiene trabajadores asignados.</p>
        <ConfirmDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)} testid="confirm-delete-project"
          title="Eliminar obra" description="La obra se eliminará. Solo es posible si no tiene trabajadores asignados."
          onConfirm={async () => { await onDelete(delId); setDelId(null); }} />
      </DialogContent>
    </Dialog>
  );
}
